import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';

import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import { useImportContext } from '../import-context';
import { useEnableLtlProductFields } from '../../../../../feature-flags/hooks/use-enable-ltl-product-fields';
import { messages } from './messages';
import styles from './styles.module.less';
import { CurrencyLabel, DimensionUnitLabel, WeightUnitLabel } from './unit-label';
import { MeasurementTypeEnum } from './unit-of-measure-type';

/**
 * @typedef {import('../../../data/types/types').TemplateAttribute} TemplateAttribute
 */

/**
 * @param {TemplateAttribute} templateAttribute
 * @returns {React.ReactNode}
 */
const getMeasurementTypeContent = (templateAttribute) => {
  switch (templateAttribute.measurementType) {
    case MeasurementTypeEnum.None:
      return null;
    case MeasurementTypeEnum.Weight:
      return <WeightUnitLabel className={styles['template-attribute--unit']} />;
    case MeasurementTypeEnum.Dimension:
      return <DimensionUnitLabel className={styles['template-attribute--unit']} />;
    case MeasurementTypeEnum.Currency:
      return <CurrencyLabel className={styles['template-attribute--unit']} />;
    default:
      return null;
  }
};

/**
 * @param {TemplateAttribute} templateAttribute
 * @returns {React.ReactNode}
 */
const getAdditionalContent = (templateAttribute) => {
  if (templateAttribute.isRequired) {
    return (
      <div className={styles['template-attribute--required']}>
        <FormattedMessage {...messages.required} />
      </div>
    );
  }
  return null;
};

/**
 * @param {TemplateAttribute} templateAttribute
 * @param {boolean} isLtlProductFieldsEnabled
 * @returns {React.ReactNode}
 */
const getTooltipContent = (templateAttribute, isLtlProductFieldsEnabled) => {
  if (templateAttribute.code !== 'upc' || !isLtlProductFieldsEnabled) {
    return null;
  }
  return (
    <Tooltip className={styles['template-attribute--tooltip']} position='bottom'>
      <TooltipContent>
        <Text className={styles['template-attribute--tooltip-text']} textType={TextType.Paragraph} value={messages.barcodeInfoTooltip} />
      </TooltipContent>
      <FontAwesomeIcon className={styles['template-attribute--tooltip-icon']} icon={faInfoCircle} />
    </Tooltip>
  );
};

/**
 * @param {object} props
 * @param {TemplateAttribute} props.templateAttribute
 * @returns {React.JSX.Element}
 */
export const ShipstationField = ({ templateAttribute }) => {
  const { translateField } = useImportContext();
  const isLtlProductFieldsEnabled = useEnableLtlProductFields();

  return (
    <span className={styles['template-field']}>
      {translateField(templateAttribute.code)}
      {getMeasurementTypeContent(templateAttribute)}
      {getAdditionalContent(templateAttribute)}
      {getTooltipContent(templateAttribute, isLtlProductFieldsEnabled)}
    </span>
  );
};
